import React, { useEffect, useState, useContext } from "react"
import {  useLocation, useNavigate  } from "react-router-dom"
import PropTypes from "prop-types"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  FormFeedback,
  Label,
} from "reactstrap"
import "./main.scss"

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"

import {
  addSystemConfiguration,
  editSystemConfiguration,
  getSystemConfigurationById,
  uploadFile,
} from 'helpers/UAM/systemConfig.helper';
import ToastDialog from "common/ToastDialog"
import { SystemConfigurationContext } from "context/systemConfigContext"

const AddSystemConfiguration = (props) => {
  const navigate = useNavigate()
  const { roleName } = JSON.parse(localStorage.getItem("user"))
  const location = useLocation()
  const pathnameSegments = location.pathname.split("/")
  const lastSegment = pathnameSegments[pathnameSegments.length - 1]
  //meta title
  let formattedLastSegment = lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).toLowerCase();
  document.title = `${formattedLastSegment} System Configuration | DMS`

  const urlParams = new URLSearchParams(window.location.search)
  const encodedId = urlParams.get("id")
  const decodedId = atob(encodedId)
  const {inputMax} = useContext(SystemConfigurationContext)

  const [systemConfigList, setSystemConfigList] = useState([])
  const [showDropZone, setShowDropZone] = useState(false)
  const [showconfigValueAsInput, setShowconfigValueAsInput] = useState(true)
  const [showViewTemplate, setShowViewTemplate] = useState(false)
  const [showEditTemplate, setShowEditTemplate] = useState(true)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)
  const [showDownloadForEdit, setShowDownloadForEdit] = useState(false)
  const [toastInfo, setToastInfo] = useState({
    show: false,
  })


  // validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      configKey: (systemConfigList[0] && systemConfigList[0].configKey) || "",
      configDescription:
        (systemConfigList[0] && systemConfigList[0].configDescription) || "",
      configValue:
        (systemConfigList[0] && systemConfigList[0].configValue) || "",
      configType: (systemConfigList[0] && systemConfigList[0].configType) || "",
    },
    validationSchema: Yup.object({
      configKey: Yup.string()
        .required("Config Key is Required")
        .test(
          "no-space",
          "Config Key cannot contain spaces",
          value => !/\s/.test(value)
        ),
      configDescription: Yup.string().required(
        "Config Description is Required"
      ),
      configType: Yup.string().required("Config Type is Required"),
      configValue: Yup.string().when("showconfigValueAsInput", {
        is: true,
        then: Yup.string().required("Config Value is Required"),
        otherwise: Yup.string(),
      }),
    }),
    onSubmit: async values => {
      if (lastSegment == "edit") {
        const updateTemplate = {
          id: decodedId,
          configKey: values.configKey,
          configDescription: values.configDescription,
          configValue: values.configValue,
          configType: values.configType,
        }
        const apiRes = await editSystemConfiguration(updateTemplate)
        if (apiRes.status) {
          navigate(`/${roleName}/system-config`)
        }
        validation.resetForm()
      } else {
        const newTemplate = {
          configKey: values.configKey,
          configDescription: values.configDescription,
          configValue: values.configValue,
          configType: values.configType,
          category: "application",
        }
        const apiRes = await addSystemConfiguration(newTemplate)
        if (apiRes.status) {
          navigate(`/${roleName}/system-config`)
          validation.resetForm()
        }
        setErrorMessage(apiRes.message)
        setUploadProgress(0)
      }
    },
  })

  const setDataInFormik = data => {
    const configData = data[0]
    validation.setFieldValue("configKey", configData.configKey)
    validation.setFieldValue("configDescription", configData.configDescription)
    validation.setFieldValue("configValue", configData.configValue)
    validation.setFieldValue("configType", configData.configType)
    if (configData.configType == "file") {
      setShowconfigValueAsInput(false)
      setShowDropZone(true)
    }
    if (lastSegment == "view") {
      setShowViewTemplate(true)
      setShowEditTemplate(false)
      setSystemConfigList(data)
    } else if (lastSegment == "edit") {
      setShowDownloadForEdit(true)
    } else {
      setShowViewTemplate(false)
      setShowEditTemplate(true)
      setShowDownloadForEdit(false)
    }
  }

  const getSystemConfigurationInfo = async () => {
    const apiRes = await getSystemConfigurationById({ id: decodedId })
    if (apiRes.status) {
      setDataInFormik(apiRes.data)
    } else {
      setToastInfo({
        showToast: true,
        message: apiRes.message,
        role: "error",
      })
    }
  }

  useEffect(() => {
    if (encodedId) {
      getSystemConfigurationInfo()
    }
  }, [encodedId])


  const navigateBack = () => {
    navigate(`/${roleName}/system-config`)
  }
  const onFileChange = async e => {
    const inputElement = e.target
    if (inputElement.files && inputElement.files.length > 0) {
      const selectedFile = inputElement.files
      if (selectedFile) {
        let formData = new FormData()
        formData.append("upl", selectedFile)
        const config = {
          onUploadProgress: progressEvent => {
            const percentage = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            setUploadProgress(percentage)
          },
        }
        try {
          setErrorMessage(null)
          const response = await uploadFile(formData, config)
        } catch (error) {
          console.error("Error uploading file:", error)
        }
      }
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Configuration"
            breadcrumbItem={`${lastSegment.toUpperCase()} System Configuration`}
          />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  {showEditTemplate && (
                    <form
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label required"
                          >
                            Config Key
                          </Label>
                          <Input
                            maxLength={inputMax}
                            type="text"
                            className="form-control"
                            name="configKey"
                            id="horizontal-url-Input"
                            placeholder="Enter Config Key"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.configKey || ""}
                            invalid={
                              validation.touched.configKey &&
                                validation.errors.configKey
                                ? true
                                : false
                            }
                          />
                          {validation.touched.configKey &&
                            validation.errors.configKey ? (
                            <FormFeedback type="invalid">
                              {validation.errors.configKey}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label required"
                          >
                            Config Type
                          </Label>
                          <Input
                             maxLength={inputMax}
                            name="configType"
                            type="select"
                            onChange={e => {
                              if (e.target.value == "file") {
                                setShowDropZone(true)
                                setShowconfigValueAsInput(false)
                              } else {
                                setShowconfigValueAsInput(true)
                                setShowDropZone(false)
                              }
                              validation.handleChange(e)
                            }}
                            onBlur={validation.handleBlur}
                            value={validation.values.configType || ""}
                            invalid={
                              validation.touched.configType &&
                                validation.errors.configType
                                ? true
                                : false
                            }
                          >
                            <option value="" disabled>
                              Select
                            </option>
                            <option value="data">Data</option>
                            <option value="file">File</option>
                          </Input>
                          {validation.touched.configType &&
                            validation.errors.configType ? (
                            <FormFeedback type="invalid">
                              {validation.errors.configType}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label required"
                          >
                            Description
                          </Label>
                          <Input
                             maxLength={inputMax}
                            type="text"
                            className="form-control"
                            name="configDescription"
                            id="horizontal-url-Input"
                            placeholder="Enter Config Description"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.configDescription || ""}
                            invalid={
                              validation.touched.configDescription &&
                                validation.errors.configDescription
                                ? true
                                : false
                            }
                          />
                          {validation.touched.configDescription &&
                            validation.errors.configDescription ? (
                            <FormFeedback type="invalid">
                              {validation.errors.configDescription}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        {showconfigValueAsInput && (
                          <Col sm={5}>
                            <Label
                              htmlFor="horizontal-notification-Input"
                              className="col-sm-3 col-form-label required"
                            >
                              Config Value
                            </Label>
                            <Input
                               maxLength={inputMax}
                              type="text"
                              className="form-control"
                              name="configValue"
                              id="horizontal-email-Input"
                              placeholder="Enter Config Value"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.configValue || ""}
                              invalid={
                                validation.touched.configValue &&
                                  validation.errors.configValue
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.configValue &&
                              validation.errors.configValue ? (
                              <FormFeedback type="invalid">
                                {validation.errors.configValue}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        )}
                      </Row>
                      <Row>
                        {showDropZone && (
                          <Col sm={10}>
                            <div>
                              <div>
                                <Label
                                  htmlFor="horizontal-email-Input"
                                  className="col-sm-6 col-form-label required"
                                >
                                  Upload File (.xlsx, .xls, .jpg, .png, .jpeg,
                                  .pdf )
                                </Label>
                                <div className="drop">
                                  <div className="text-wrapper">
                                    <div className="centered">
                                      <input
                                        type="file"
                                        name="formatDocument"
                                        id="fileExcel"
                                        accept=".jpg, .png, .jpeg, .pdf, .xlsx, .xls"
                                        onChange={onFileChange}
                                      />
                                      <label
                                        style={{
                                          marginBottom: 0,
                                          fontSize: "14px",
                                        }}
                                        htmlFor="fileExcel"
                                      >
                                        <img
                                          src="https://scfuat.cashinvoice.in/frontend/assets/themes/image/icon/cloud.png"
                                          className="img-fluid"
                                          style={{ width: "30px" }}
                                          alt="Upload"
                                        />
                                        <span className="textLink">
                                          Browse files to attach
                                        </span>
                                        or Drop
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                {uploadProgress > 0 && !errorMessage && (
                                  <div className="progress mt-2">
                                    <div
                                      className="progress-bar bg-success"
                                      role="progressbar"
                                      style={{ width: `${uploadProgress}%` }}
                                      aria-valuenow="0"
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    >
                                      {`${uploadProgress}%`}
                                    </div>
                                  </div>
                                )}
                                {errorMessage && (
                                  <div className="alert alert-danger mt-2">
                                    {errorMessage}
                                  </div>
                                )}
                              </div>
                              <hr />
                            </div>
                          </Col>
                        )}
                        {showDownloadForEdit && (
                          <div>

                            {systemConfigList[0]?.configType == "file" ? (
                              <div className="d-flex">
                                <div className="mt-1"> {systemConfigList[0]?.configValue}</div>
                                <i
                                  className="mdi mdi-download downloadFile"
                                  style={{ marginLeft: "20px" }}
                                  onClick={() => {
                                    downloadSystemConfigFile(
                                      systemConfigList[0].configValue
                                    )
                                  }}
                                ></i>

                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        )}
                      </Row>
                      <Row>
                        <Col>
                          <div
                            className="text-end"
                            style={{ margin: "20px 17% 0px 0px" }}
                          >
                            <button
                              type="submit"
                              className="btn btn-outline-secondary"
                              style={{ marginLeft: "7px" }}
                              onClick={() => {
                                navigateBack()
                              }}
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              className="btn btn-success save-customer"
                              style={{ marginLeft: "7px" }}
                            >
                              Save
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </form>
                  )}
                  {showViewTemplate && (
                    <div>
                      <Row className="mb-4">
                        <Col sm={5} md={4}>
                          <Label className="col-sm-3 col-form-label label label-styling ">
                            Config Key
                          </Label>

                          {systemConfigList.length > 0 ? (
                            <p>
                              {systemConfigList[0].configKey}
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                        <Col sm={5} md={4}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label label label-styling "
                          >
                            Config Type
                          </Label>
                          {systemConfigList.length > 0 ? (
                            <p>
                              {systemConfigList[0].configType}
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                      
                        <Col sm={5} md={4}>
                          <Label className="col-sm-3 col-form-label label label-styling ">
                            Description
                          </Label>

                          {systemConfigList.length > 0 ? (
                            <p>
                              {systemConfigList[0].configDescription}
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                        <Col sm={5} md={4}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label label label-styling "
                          >
                            Config value
                          </Label>
                          {systemConfigList.length > 0 ? (
                            <div>
                              <p>
                                {systemConfigList[0].configValue}
                              </p>
                              <div>
                                {systemConfigList[0].configType == "file" ? (
                                  <i
                                    className="mdi mdi-download downloadFile "
                                    onClick={() => {
                                      downloadSystemConfigFile(
                                        systemConfigList[0].configValue
                                      )
                                    }}
                                  ></i>
                                ) : (
                                  ""
                                )}
                              </div>
                            </div>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                      
                        <Col sm={5} md={4}>
                          <Label className="col-sm-3 col-form-label label label-styling">
                            Category
                          </Label>

                          {systemConfigList.length > 0 ? (
                            <p>
                              {systemConfigList[0].category}
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                navigateBack()
                              }}
                            >
                              Back
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastDialog
        message={toastInfo.message || ""}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      ></ToastDialog>
    </React.Fragment>
  )
}

AddSystemConfiguration.propTypes = {
  history: PropTypes.object,
}

export default AddSystemConfiguration
